import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  RepeatScopeProvider,
  useDocument,
  useIsReadOnly,
  useRepeatScope,
} from '../DocumentView/DocumentContext';
import DocumentElement from '../DocumentView/DocumentElement';
import { FieldHeader, FieldHelpText } from './FieldParts';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';

interface Props {
  element: DocumentElementType;
}

const toPositiveInt = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/**
 * Line Items (Elements Library): a repeating row group. Each row is an
 * independent instance of the child elements, scoped via RepeatScopeProvider
 * so child fields read/write into that row's slice. The filler adds and
 * removes rows; value is stored as an array of per-row dicts.
 */
const LineItems: React.FC<Props> = ({ element }) => {
  const { getAtPath, setAtPath } = useDocument();
  const isReadOnly = useIsReadOnly();
  const parentScope = useRepeatScope();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    maxRepeats?: number | string;
    repeatItemName?: string;
  };

  const path = [...parentScope, element.id];
  const rawArray = getAtPath(path);
  const instances: Record<string, unknown>[] = Array.isArray(rawArray)
    ? (rawArray as Record<string, unknown>[])
    : [{}];

  const limit = toPositiveInt(data?.maxRepeats) || Infinity;
  const canAdd = !isReadOnly && instances.length < limit;
  const canRemove = !isReadOnly && instances.length > 1;
  const itemName = data?.repeatItemName || 'item';
  const children = element.elements || [];

  const addInstance = useCallback(() => {
    setAtPath(path, [...instances, {}]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances, setAtPath, JSON.stringify(path)]);

  const removeInstance = useCallback(
    (idx: number) => {
      const next = instances.filter((_, i) => i !== idx);
      setAtPath(path, next.length > 0 ? next : [{}]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instances, setAtPath, JSON.stringify(path)],
  );

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={data?.label || 'Line items'}
        subLabel={data?.subLabel}
        required={!!data?.required}
      />
      <div className="overflow-hidden rounded-[13px] border border-[#e6e9ee]">
        {instances.map((_, idx) => (
          <div
            key={idx}
            className={
              idx < instances.length - 1
                ? 'flex items-start gap-[10px] border-b border-[#eef1f4] px-[13px] py-[11px]'
                : 'flex items-start gap-[10px] px-[13px] py-[11px]'
            }
          >
            <span className="mt-[2px] flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[6px] bg-[var(--forms-accent-soft,var(--forms-accent-soft-default))] text-[11px] font-extrabold text-[var(--forms-accent,var(--forms-accent-default))]">
              {idx + 1}
            </span>
            <RepeatScopeProvider sectionId={element.id} index={idx}>
              <div className="grid flex-1 gap-[9px] [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
                {children.length === 0 ? (
                  <p className="py-[2px] text-[12.5px] italic text-[var(--forms-muted-soft)]">
                    Add fields to this line-items group in the designer.
                  </p>
                ) : (
                  children.map((child) => (
                    <DocumentElement key={child.id} element={child} />
                  ))
                )}
              </div>
            </RepeatScopeProvider>
            {canRemove && (
              <button
                type="button"
                onClick={() => removeInstance(idx)}
                aria-label={`Remove ${itemName} ${idx + 1}`}
                className="mt-[3px] flex-none text-[var(--forms-optional)] transition-colors hover:text-[var(--forms-rail-red)]"
              >
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 15 }} />
              </button>
            )}
          </div>
        ))}
        {canAdd && (
          <div className="px-[13px] py-[9px]">
            <button
              type="button"
              onClick={addInstance}
              className="inline-flex items-center gap-[6px] rounded-[8px] bg-[var(--forms-accent-soft,var(--forms-accent-soft-default))] px-[13px] py-[8px] text-[12.5px] font-bold text-[var(--forms-accent,var(--forms-accent-default))] transition-opacity hover:opacity-80"
            >
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: 12 }} />
              Add {itemName}
            </button>
          </div>
        )}
      </div>
      <FieldHelpText>{data?.helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default LineItems;
